import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  Box,
  Checkbox,
} from "@mui/material";

import {
  ApiType,
  FilterSelectMultipleApi,
  Item,
  Option,
  OptionList,
} from "../interfaces";
import { GenericObject } from "../../services/Price";
import { useDebounce } from "../../hooks/debounce";
import { SCROLL_BAR_STYLES_THIN } from "../../Theme";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { SX_CONTROLLED_AUTOCOMPLETE_SIZE } from "../styles";
import { isObject } from "../../services/utils";
import { getSelectFilterValues } from "../utils";

const useStyles = makeStyles()(() => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    width: "270px",
  },
  scrollbar: SCROLL_BAR_STYLES_THIN,
}));

const useGetWatchLookupValue = (
  filters: GenericObject,
  filter: FilterSelectMultipleApi
) => (filter.watchLookup ? filters[filter.watchLookup] : false);

/**
 * SelectMultipleApiFilter component.
 */
const SelectMultipleApiFilter = ({
  filters,
  filter,
  onChange,
}: {
  filters: GenericObject;
  filter: FilterSelectMultipleApi;
  onChange: (newValue: any) => void;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const [options, setOptions] = useState<OptionList>([]);
  const [initialValues, setInitialValues] = useState<string | null>(null);
  const [newValues, setNewValues] = useState<Option[]>([]);
  const [apiData, setApiData] = useState<ApiType | null>(null);
  const [apiUrl, setApiUrl] = useState("");
  const [configParams, setConfigParams] = useState<GenericObject>(() => {
    const params: GenericObject = {};

    if (apiData && apiData.apiParams) {
      for (const [key, value] of Object.entries(apiData.apiParams)) {
        //Simple update
        if (!isObject(value)) {
          params[key] = value;
        }
      }
    }

    return params;
  });

  const lookupValue: string | false = useGetWatchLookupValue(filters, filter);
  const debounceSearchValue = useDebounce(initialValues, 400);

  const query = useQuery(
    [apiUrl, configParams, debounceSearchValue],
    () =>
      getSelectFilterValues<Item>(apiUrl, configParams, debounceSearchValue),
    { enabled: !!apiUrl }
  );

  useEffect(() => {
    if (filter.watchLookup) {
      onChange([]);
      setNewValues([]);
    }
  }, [onChange, filter, lookupValue]);

  // Generate api params if exist
  useEffect(() => {
    const params: GenericObject = {};

    if (apiData && apiData.apiParams) {
      for (const [key, value] of Object.entries(apiData.apiParams)) {
        //Simple update
        if (!isObject(value)) {
          params[key] = value;
        }
      }
    }

    setConfigParams(params);
  }, [apiData]);

  // Generate selector options
  useEffect(() => {
    if (query.data && apiData) {
      /**
       * Due to mismatch between what we display and what we need to send to backend if option is "ALL" ("Tous" and "Tout"), we add key param
       * id => is for Mui differentiation between option
       * label => what is displayed in selector
       * key => what we send to backend
       */
      const generateData = query.data.results.map((option) => ({
        id: option.id,
        label: option[apiData.labelKey],
        key: apiData.dataKey ? option[apiData.dataKey] : option.id,
      }));

      setOptions(generateData);
    } else if (apiData && apiData.defaultValue && apiData.api === "") {
      setOptions([
        {
          id: 0,
          label: intl.formatMessage({ id: apiData.defaultValue }),
          key:
            apiData.defaultValue === "ALL"
              ? "ALL"
              : intl.formatMessage({ id: apiData.defaultValue }),
        },
      ]);
    }
  }, [query.data, apiData, intl]);

  // Select api data according filter value to watch
  useEffect(() => {
    const watchFilterValue = filters[filter.multiApi.watchFilter];
    const apiData = filter.multiApi.apis.filter(
      (el) => el.caseOfValueIs === watchFilterValue
    );

    if (apiData[0]) {
      setApiData(apiData[0]);
      setApiUrl(apiData[0].api);
    }
  }, [filter.multiApi.watchFilter, filter.multiApi.apis, filters]);

  /**
   * Handle change Option.
   */
  const handleChange = (
    values: Option[],
    details: AutocompleteChangeDetails<Option> | undefined
  ): void => {
    setNewValues(values);
    onChange(values);
  };

  /**
   * Set placeholder on initial render and on input change if input is empty.
   */
  const handleInputChange = (
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ): void => {
    if (reason !== "reset") {
      setInitialValues(newInputValue);
    }
  };

  return (
    <Box className={basicsClasses.field}>
      <CustomAutocomplete
        multiple
        limitTags={
          filter.maxTag && !filter.controlledInputSize
            ? filter.maxTag
            : filter.controlledInputSize
            ? 1
            : undefined
        }
        disableCloseOnSelect
        disabled={!!filter.watchLookup && !lookupValue}
        getOptionLabel={(option) => {
          return option.label || "";
        }}
        isOptionEqualToValue={(option: Option, value: Option) =>
          option.id === value.id
        }
        loading={query.isLoading}
        options={options}
        onChange={(_event, values, _reason, details) =>
          handleChange(values, details)
        }
        onInputChange={(_event, newInputValue, reason) =>
          handleInputChange(newInputValue, reason)
        }
        placeholder={intl.formatMessage({ id: filter.label })}
        renderOptions={(props, option, state) => (
          <li {...props} key={option.id}>
            <Checkbox sx={{ mr: 1, p: 0 }} checked={state.selected} />
            {option.label}
          </li>
        )}
        size="small"
        sx={{
          ...(filter.controlledInputSize
            ? SX_CONTROLLED_AUTOCOMPLETE_SIZE
            : {}),
          "& .MuiFormControl-root .MuiInputBase-root": {
            "& .MuiInputBase-input": {
              padding: "2px 4px 3px 0",
            },
          },
          "& .MuiAutocomplete-inputRoot": {
            height: "unset",
          },
        }}
        value={newValues}
        variant="standard"
      />
    </Box>
  );
};

export default SelectMultipleApiFilter;
